import { useCallback, useEffect, useRef, useState } from 'react'
import { findCreatedLinkages, getCreatedResponseIds, getCreatedLinkageDiagnostics } from './createdLinkage.utils'

// Six fast reads exhausted the retries after only 23 seconds, before the
// availability timeout. Keep polling through the reads at 31 and 39 seconds.
export const MAX_AVAILABILITY_READS = 8
const AVAILABILITY_TIMEOUT_MS = 45000

export const useBankLinkageCreation = ({ loadRows, onCreated, onStatusChange, onCompleted, onError, onStart, bankMovementType }) => {
  const handlers = useRef({ loadRows, onCreated, onStatusChange, onCompleted, onError, onStart, bankMovementType })
  handlers.current = { loadRows, onCreated, onStatusChange, onCompleted, onError, onStart, bankMovementType }
  const queue = useRef(Promise.resolve())
  const inFlight = useRef(new Map())
  const batchFailed = useRef(false)
  // Preserve successful creations if a later step fails, so saving again cannot duplicate them.
  const pending = useRef(new Map())
  const [status, updateStatus] = useState(null)
  const controller = useRef(new AbortController())
  useEffect(() => {
    const active = new AbortController()
    controller.current = active
    return () => active.abort()
  }, [])
  const setStatus = useCallback(value => {
    updateStatus(value)
    handlers.current.onStatusChange?.(value)
  }, [])

  const runCreation = useCallback((request, { payload, model } = {}) => {
    if (batchFailed.current) return Promise.reject(new Error('El proceso fallo. Cierra el aviso y revisa la vinculacion antes de continuar.'))
    const movementType = handlers.current.bankMovementType
    const key = JSON.stringify({ payload, model, bankMovementType: movementType })
    if (inFlight.current.has(key)) return inFlight.current.get(key)
    if (!inFlight.current.size) {
      batchFailed.current = false
      handlers.current.onError?.('')
      handlers.current.onStart?.()
    }
    const execute = async () => {
      let diagnostics = null
      if (batchFailed.current) throw new Error('El proceso fallo; no se crearan mas vinculaciones en esta sesion.')
      const signal = controller.current.signal
      const ensureActive = () => {
        if (signal.aborted) throw new Error('Proceso de vinculacion interrumpido.')
      }
      ensureActive()
      const waitForNextRead = delay => new Promise(resolve => {
        const finish = () => {
          clearTimeout(timer)
          signal.removeEventListener('abort', finish)
          resolve()
        }
        const timer = setTimeout(finish, delay)
        signal.addEventListener('abort', finish, { once: true })
      })
      setStatus('Procesando la creación de la vinculación…')
      try {
        let task = pending.current.get(key)
        if (!task) {
          const before = await handlers.current.loadRows(payload, { signal, timeout: 10000 })
          ensureActive()
          const response = await request()
          const body = response?.data ?? response
          if (body?.success === false || body?.error || Number(body?.statusCode) >= 400) {
            throw new Error(body?.message || 'No se pudo crear la vinculación.')
          }
          task = { before, response, payload, model, bankMovementType: movementType }
          pending.current.set(key, task)
        }
        ensureActive()
        setStatus('Esperando que la vinculación esté disponible…')
        let created = task.created
        let after = task.after
        if (!created) {
          let attempt = 0
          const deadline = Date.now() + AVAILABILITY_TIMEOUT_MS
          while (!created?.length && attempt < MAX_AVAILABILITY_READS && Date.now() < deadline) {
            ensureActive()
            attempt++
            try {
              after = await handlers.current.loadRows(payload, {
                signal,
                timeout: Math.max(1, Math.min(8000, deadline - Date.now()))
              })
              ensureActive()
              created = findCreatedLinkages({ ...task, after })
            } catch (failure) {
              ensureActive()
              if (failure.code === 'INVALID_LINKAGE_RESPONSE' || failure instanceof TypeError) throw failure
              const httpStatus = failure.response?.status
              // Authentication/validation errors need intervention; pending rows and
              // transient read failures keep polling without repeating the creation.
              if (httpStatus >= 400 && httpStatus < 500 && ![408, 429].includes(httpStatus)) throw failure
            }
            if (!created?.length && attempt < MAX_AVAILABILITY_READS && Date.now() < deadline) {
              await waitForNextRead(Math.min(1000 * 2 ** (attempt - 1), 8000, deadline - Date.now()))
            }
          }
          if (!created?.length) {
            diagnostics = getCreatedLinkageDiagnostics({ ...task, after: after ?? [] })
            // Log structural evidence without party data.
            console.warn('[Conciliación Banco] No se pudo resolver el ID contable', {
              model,
              idsRespuestaCreacion: [...getCreatedResponseIds(task.response)],
              idsCoincidentesPorDatos: findCreatedLinkages({ ...task, after: after ?? [], response: {} })
                .map(row => row.idPrincipal ?? row.id),
              registrosAntes: task.before.length,
              registrosDespues: after?.length ?? 0,
              diagnostics
            })
            throw new Error('No pudimos identificarla en la lista del banco y detuvimos las consultas. Cierra este aviso y revisa la vinculacion existente antes de conciliarla desde el banco.')
          }
          Object.assign(task, { created, after })
        }
        setStatus('Guardando la conciliación…')
        await handlers.current.onCreated(created, after)
        pending.current.delete(key)
        ensureActive()
        return task.response
      } catch (failure) {
        batchFailed.current = true
        if (!signal.aborted) {
          setStatus(null)
          handlers.current.onError?.(pending.current.has(key)
            ? 'La vinculación se creó correctamente, pero no fue posible conciliarla con el movimiento bancario. Revisa la vinculación existente e intenta conciliarla nuevamente desde el banco.'
            : `No se pudo crear la vinculación. ${failure.response?.data?.message || failure.message || 'Revisa los datos.'}`, diagnostics)
        }
        throw failure
      }
    }
    const result = queue.current.then(execute).finally(() => {
      inFlight.current.delete(key)
      if (!inFlight.current.size && !controller.current.signal.aborted) {
        setStatus(null)
        if (!batchFailed.current && !pending.current.size) handlers.current.onCompleted?.()
      }
    })
    inFlight.current.set(key, result)
    queue.current = result.catch(() => {})
    return result
  }, [setStatus])

  return { runCreation, status, isProcessing: Boolean(status) }
}
